<script setup lang="ts">
/**
 * ElectionDetailsPanel — Thin Router
 *
 * Routes between mobile (<1024px) and desktop (>=1024px) panel implementations.
 * Maintains the same props/emits API so ElectionHub.vue needs no changes.
 *
 * @see ElectionDetailsMobile.vue — bottom Drawer with compact layout
 * @see ElectionDetailsDesktop.vue — centered Dialog with full layout
 */
import { useMediaQuery } from '@vueuse/core';
import ElectionDetailsMobile from './ElectionDetailsMobile.vue';
import ElectionDetailsDesktop from './ElectionDetailsDesktop.vue';
import type { ForecastTraderElectionMapProduct } from '@/types/forecasttrader.types';

defineProps<{
  visible: boolean;
  products: ForecastTraderElectionMapProduct[];
  initialProductConid?: number;
  initialCandidateConid?: number;
  /** Leading party from API ('Democrat', 'Republican', etc.) */
  party?: string;
  /** Color intensity from API (0=toss-up, 1=lean, 2=likely, 3=solid) */
  color?: number;
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
  /** Share this election. When `conid` is provided, share targets a single candidate; otherwise share is product-level. */
  share: [conid?: number];
  toggleWatchlist: [];
  showGrid: [options?: { filters?: string }];
}>();

const isDesktop = useMediaQuery('(min-width: 1024px)');
</script>

<template>
  <ElectionDetailsDesktop
    v-if="isDesktop"
    :visible="visible"
    :products="products"
    :initial-product-conid="initialProductConid"
    :initial-candidate-conid="initialCandidateConid"
    :party="party"
    :color="color"
    @update:visible="emit('update:visible', $event)"
    @share="emit('share', $event)"
    @toggle-watchlist="emit('toggleWatchlist')"
    @show-grid="emit('showGrid', $event)"
  />
  <ElectionDetailsMobile
    v-else
    :visible="visible"
    :products="products"
    :initial-product-conid="initialProductConid"
    :initial-candidate-conid="initialCandidateConid"
    :party="party"
    :color="color"
    @update:visible="emit('update:visible', $event)"
    @share="emit('share', $event)"
    @toggle-watchlist="emit('toggleWatchlist')"
    @show-grid="emit('showGrid', $event)"
  />
</template>
